import Typography from '@components/v2/ui/Typography/Typography';
import { useRef } from 'react';
import LangEnum from '../../../../../../lang/LangEnum';
import OrderProductPriceV2 from '../../../../../orders/products/product/components/OrderProductPriceV2';
import OrderProductGridContainer from '../../../../../orders/products/product/grid/components/orderProductGrid/components/grid/components/OrderProductGridContainer';
import CombinationLineUnavailableGrid from '../../../../../products/products-details-v2/components/CombinationLine/components/CombinationLine/CombinationLineUnavailableGrid';
import { useTranslation } from '../../../../../translation/TranslationContext';
import { CatalogSectionProductColorV2 } from '../../../interfaces/catalogProductList.types';
import { RowCard } from '../../catalogProductsListV2.types';
import { useCatalogDraggableItem } from '../../hooks/useCatalogDraggableItem';
import useCatalogProductListRow from '../hooks/useCatalogProductListRow';
import { toCatalogGridContainerProps } from '../utils/catalogGridContainerProps.transformer';
import { measureCatalogListDragRect } from '../utils/catalogListDragRect.utils';
import CatalogListAddOrderButton from './CatalogListAddOrderButton';
import CatalogListCombinationChips from './CatalogListCombinationChips';
import CatalogProductListRow, { CatalogListRowAdjacentSelection } from './CatalogProductListRow';
import CatalogProductListRowLeftBlock from './CatalogProductListRowLeftBlock';

export interface CatalogListRowDragConfig {
  isDraggingDisabled: boolean;
}

interface CatalogProductListRowConnectedProps {
  catalogId: string;
  card: Extract<RowCard, { kind: 'item' }>;
  productColor: CatalogSectionProductColorV2;
  isStacked: boolean;
  adjacentSelection: CatalogListRowAdjacentSelection;
  drag?: CatalogListRowDragConfig;
}

const CatalogProductListRowConnected = ({
  catalogId,
  card,
  productColor,
  isStacked,
  adjacentSelection,
  drag,
}: CatalogProductListRowConnectedProps) => {
  const { translate } = useTranslation();
  const leftBlockRef = useRef<HTMLDivElement | null>(null);
  const row = useCatalogProductListRow({ catalogId, card, productColor, isStacked });

  const leftBlock = { isStacked, ...row.leftBlock };

  const { attributes, listeners, setActivatorNodeRef, setNodeRef } = useCatalogDraggableItem({
    id: card.esProduct.id,
    isDraggingDisabled: !drag || drag.isDraggingDisabled,
    data: {
      listRow: {
        leftBlock,
        measureRect: () => measureCatalogListDragRect(leftBlockRef.current),
      },
    },
  });

  const setActivatorRef = (node: HTMLDivElement | null) => {
    leftBlockRef.current = node;
    setActivatorNodeRef(node);
  };

  const addButton =
    row.hasGrid && row.addOrderButtonProps ? (
      <CatalogListAddOrderButton {...row.addOrderButtonProps} />
    ) : undefined;

  const renderGrid = () => {
    if (!row.hasGrid || !row.gridParams) {
      return <CombinationLineUnavailableGrid gridLength={0} />;
    }

    return (
      <OrderProductGridContainer
        key={row.gridKey}
        {...toCatalogGridContainerProps({ ...row.gridParams, addButton })}
      />
    );
  };

  const renderPrice = () => {
    if (!row.selectedCombinationKey || !row.hasGrid || row.price.shouldDisableAllPrices) {
      return null;
    }

    if (row.price.disableWholesalePrice) {
      return null;
    }

    if (!row.price.hasPrices) {
      return (
        <Typography noWrap variant="body2" color="textSecondary">
          {translate(LangEnum.PRODUCT_DETAILS_UNAVAILABLE_GRID)}
        </Typography>
      );
    }

    return (
      <OrderProductPriceV2
        grid={row.price.grid}
        combinationKey={row.selectedCombinationKey}
        partsQuantity={row.price.partsQuantity}
        showTotal={row.price.showTotal}
        hidePricesTooltip={row.price.hidePricesTooltip}
      />
    );
  };

  const combinations = row.selectedCombinationKey ? (
    <CatalogListCombinationChips
      {...row.combinationStrip}
      selectedKey={row.selectedCombinationKey}
      onSelect={(combination) => row.onSelectCombination(combination.combinationKey)}
    />
  ) : undefined;

  return (
    <CatalogProductListRow
      isStacked={isStacked}
      rowRef={setNodeRef}
      selection={row.selection && { ...row.selection, ...adjacentSelection }}
      combinations={combinations}
      addButton={addButton}
      dragProps={drag && { attributes, listeners, activatorRef: setActivatorRef }}
      left={<CatalogProductListRowLeftBlock {...leftBlock} />}
      grid={renderGrid()}
      price={renderPrice()}
    />
  );
};

export default CatalogProductListRowConnected;
